import { useCallback, useEffect, useRef, useState } from "react";
import useTitlesService, {
  TitleImportInProgressError,
} from "../../services/TitlesService";
import {
  TitleImportRun,
  TitleImportStatus,
  isTitleImportRunActive,
} from "../../types/TitleImport";
import PositiveButton from "../PositiveButton";
import NegativeButton from "../NegativeButton";
import TitleAuditSection from "./TitleAuditSection";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

/** How often the status is refreshed while a run is in flight. */
const ACTIVE_POLL_MS = 5000;

/** Remembers whether the panel was left open, so it does not reset on every navigation. */
const EXPANDED_STORAGE_KEY = "beamxr.titleImportPanel.expanded";

const toDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

const formatDateTime = (value?: string) =>
  value ? new Date(value).toLocaleString() : "-";

const formatDate = (value: string) => new Date(value).toLocaleDateString();

const statusClasses: Record<string, string> = {
  Queued: "bg-amber-200 text-amber-900",
  Running: "bg-blue-200 text-blue-900",
  Completed: "bg-green-200 text-green-900",
  Failed: "bg-red-200 text-red-900",
  Cancelled: "bg-gray-200 text-gray-900",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`px-2 py-1 rounded text-xs ${
        statusClasses[status] ?? "bg-gray-200 text-gray-900"
      }`}
    >
      {status}
    </span>
  );
}

/** The one line worth seeing when the panel is closed. */
function RunSummary({ run }: { run: TitleImportRun }) {
  return (
    <div
      className="flex flex-row gap-2 items-center text-sm"
      title={run.errorMessage ?? undefined}
    >
      <StatusBadge status={run.status} />
      <span className="hidden sm:inline opacity-80">
        {run.source} &middot; {formatDate(run.fromDate)} to{" "}
        {formatDate(run.toDate)}
      </span>
      {run.status === "Completed" && (
        <span className="opacity-80">
          +{run.titlesCreated} added, {run.titlesUpdated} updated,{" "}
          {run.titlesUnchanged} unchanged
        </span>
      )}
    </div>
  );
}

function RunProgress({ run }: { run: TitleImportRun }) {
  const percent =
    run.progressTotal > 0
      ? Math.min(100, Math.round((run.progressCurrent / run.progressTotal) * 100))
      : 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row justify-between text-sm">
        <span>{run.progressStage ?? "Starting"}</span>
        <span>
          {run.progressTotal > 0
            ? `${run.progressCurrent} of ${run.progressTotal}`
            : "Working"}
        </span>
      </div>
      <div className="w-full h-2 bg-cyan-900/40 rounded overflow-hidden">
        <div
          className="h-full bg-cyan-400 transition-all duration-500"
          // A stage with no known total still needs to look alive, so show a sliver.
          style={{ width: `${run.progressTotal > 0 ? percent : 5}%` }}
        />
      </div>
    </div>
  );
}

export default function TitleImportPanel() {
  const { startTitleImport, getTitleImportStatus, cancelTitleImport } =
    useTitlesService();

  const today = toDateInputValue(new Date());

  const [status, setStatus] = useState<TitleImportStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    fromDate: today,
    toDate: today,
  });

  // Closed by default: the page is the titles list, and importing is an occasional job.
  const [isExpanded, setIsExpanded] = useState(
    () => window.localStorage.getItem(EXPANDED_STORAGE_KEY) === "true"
  );

  const toggleExpanded = () => {
    setIsExpanded((previous) => {
      window.localStorage.setItem(EXPANDED_STORAGE_KEY, (!previous).toString());
      return !previous;
    });
  };

  // Held in a ref so the polling effect can read the latest value without being torn down and
  // restarted every time the status changes.
  const isLoadingRef = useRef(false);

  const refreshStatus = useCallback(async () => {
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setIsLoading(true);

    try {
      const result = await getTitleImportStatus();
      setStatus(result);
      setError(null);
    } catch (err) {
      console.error("Error fetching title import status:", err);
      setError("Could not load the import status.");
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const activeRun = status?.activeRun;

  useEffect(() => {
    if (!activeRun) {
      return;
    }

    // Only poll while something is actually running.
    const handle = setInterval(refreshStatus, ACTIVE_POLL_MS);

    return () => clearInterval(handle);
  }, [activeRun, refreshStatus]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const rangeIsValid =
    formData.fromDate.length > 0 &&
    formData.toDate.length > 0 &&
    formData.fromDate <= formData.toDate &&
    formData.toDate <= today;

  const start = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isStarting || activeRun || !rangeIsValid) {
      return;
    }

    setIsStarting(true);
    setError(null);
    setMessage(null);

    try {
      await startTitleImport(formData.fromDate, formData.toDate);
      // The runner is a cron job on a five minute tick, not a live worker, so promising "a few
      // seconds" here would be wrong.
      setMessage("Import queued. It will begin within about five minutes.");
    } catch (err) {
      if (err instanceof TitleImportInProgressError) {
        setError("An import is already running. Wait for it to finish first.");
      } else {
        setError("Could not start the import.");
      }
    } finally {
      setIsStarting(false);
      await refreshStatus();
    }
  };

  const cancel = async () => {
    if (!activeRun) {
      return;
    }

    setError(null);
    setMessage(null);

    try {
      await cancelTitleImport(activeRun.id);
      setMessage("Cancellation requested. Titles already imported are kept.");
    } catch (err) {
      console.error("Error cancelling title import:", err);
      setError("Could not cancel the import.");
    } finally {
      await refreshStatus();
    }
  };

  // recentRuns comes back newest first and includes whatever is in flight, so the head of it is
  // the run worth showing when the panel is closed.
  const latestRun = status?.recentRuns?.[0];

  // The audit works through the catalogue a pass at a time, so the last completed report is what
  // says where it has got to.
  const lastAuditRun = status?.recentRuns?.find(
    (r) => r.source === "Audit" && r.status === "Completed"
  );

  return (
    <section className="card p-4 flex flex-col gap-4">
      <button
        type="button"
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        className="flex flex-row gap-2 items-center text-left w-full"
        title={isExpanded ? "Hide import options" : "Show import options"}
      >
        <CloudDownloadIcon />
        <h2 className="text-xl">Import titles from IGDB</h2>

        {/* The latest run stays on the header line so it is visible either way. */}
        <div className="ml-auto flex flex-row gap-3 items-center">
          {latestRun && <RunSummary run={latestRun} />}
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </div>
      </button>

      {error && (
        <div className="p-3 rounded bg-red-100 text-red-900 text-sm">{error}</div>
      )}

      {message && (
        <div className="p-3 rounded bg-blue-100 text-blue-900 text-sm">
          {message}
        </div>
      )}

      {/* A running import shows its progress whether the panel is open or not: it is the one thing
          worth interrupting the page for. */}
      {activeRun && (
        <div className="flex flex-col gap-3 p-3 rounded border border-cyan-900">
          <div className="flex flex-row gap-2 items-center justify-between">
            <div className="flex flex-row gap-2 items-center">
              <StatusBadge status={activeRun.status} />
              <span className="text-sm">
                {activeRun.source} import covering{" "}
                {formatDate(activeRun.fromDate)} to {formatDate(activeRun.toDate)}
              </span>
            </div>

            <NegativeButton onClick={cancel} disabled={activeRun.cancelRequested}>
              {activeRun.cancelRequested ? "Cancelling" : "Cancel"}
            </NegativeButton>
          </div>

          <RunProgress run={activeRun} />
        </div>
      )}

      {isExpanded && (
        <>
        <form onSubmit={start} className="flex flex-col lg:flex-row gap-4 lg:items-end">
          <div className="flex flex-col gap-2">
            <label htmlFor="fromDate">From</label>
            <input
              type="date"
              id="fromDate"
              name="fromDate"
              max={today}
              value={formData.fromDate}
              onChange={handleInputChange}
              className="border p-2 rounded-md text-black"
              required
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="toDate">To</label>
            <input
              type="date"
              id="toDate"
              name="toDate"
              max={today}
              value={formData.toDate}
              onChange={handleInputChange}
              className="border p-2 rounded-md text-black"
              required
            />
          </div>

          <PositiveButton
            type="submit"
            disabled={isStarting || !!activeRun || !rangeIsValid}
            title={
              activeRun
                ? "An import is already running."
                : !rangeIsValid
                ? "Pick a range that ends today or earlier."
                : "Start the import"
            }
          >
            {isStarting ? "Starting..." : "Start import"}
          </PositiveButton>
        </form>

        {!rangeIsValid && (
          <p className="text-sm text-amber-300">
            The range must start on or before it ends, and cannot reach into the
            future.
          </p>
        )}

        <div className="flex flex-col gap-2">
          <h3 className="text-lg">Recent imports</h3>

          <div className="overflow-x-auto">
            <table className="table-auto w-full text-sm">
              <thead>
                <tr>
                  <th className="font-normal text-left p-2">Status</th>
                  <th className="font-normal text-left p-2">Source</th>
                  <th className="font-normal text-left p-2">Range</th>
                  <th className="font-normal text-left p-2">Queued</th>
                  <th className="font-normal text-right p-2">Found</th>
                  <th className="font-normal text-right p-2">Added</th>
                  <th className="font-normal text-right p-2">Updated</th>
                  <th className="font-normal text-right p-2">Unchanged</th>
                  <th className="font-normal text-right p-2">Skipped</th>
                  <th className="font-normal text-right p-2">Failed</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && !status && (
                  <tr className="border-t border-cyan-900">
                    <td className="p-2" colSpan={10}>
                      Loading...
                    </td>
                  </tr>
                )}

                {status && status.recentRuns.length === 0 && (
                  <tr className="border-t border-cyan-900">
                    <td className="p-2" colSpan={10}>
                      No imports have run yet.
                    </td>
                  </tr>
                )}

                {status?.recentRuns.map((run) => (
                  <tr
                    key={run.id}
                    className={`border-t border-cyan-900 ${
                      isTitleImportRunActive(run) ? "opacity-100" : "opacity-80"
                    }`}
                    title={run.errorMessage ?? undefined}
                  >
                    <td className="p-2">
                      <StatusBadge status={run.status} />
                    </td>
                    <td className="p-2">{run.source}</td>
                    <td className="p-2">
                      {formatDate(run.fromDate)} to {formatDate(run.toDate)}
                    </td>
                    <td className="p-2">{formatDateTime(run.queuedAt)}</td>
                    <td className="p-2 text-right">{run.gamesDiscovered}</td>
                    <td className="p-2 text-right">{run.titlesCreated}</td>
                    <td className="p-2 text-right">{run.titlesUpdated}</td>
                    {/* Nearly the whole run on a settled catalogue. A run that keeps
                        reporting updates instead is rewriting rows that have not changed. */}
                    <td className="p-2 text-right opacity-60">
                      {run.titlesUnchanged}
                    </td>
                    <td className="p-2 text-right">
                      {run.titlesSkippedContent + run.titlesSkippedStatus}
                    </td>
                    <td className="p-2 text-right">{run.titlesFailed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <TitleAuditSection
          isRunActive={!!activeRun}
          lastAuditRun={lastAuditRun}
          onRunStarted={refreshStatus}
        />
        </>
      )}
    </section>
  );
}
